/**
 * Narrow data-source hook used by the Invite modal on `/settings/access`
 * (task 104d.1 mockup, 104d.2 live). Looks up an Employee by email as the
 * admin types, so the modal can offer a "link on acceptance" callout when
 * the invite email matches an existing unlinked Employee (Q5D).
 *
 * Kept deliberately separate from `useOrgAccess` — 104d.2's dedicated
 * `GET /api/v1/orgs/:orgId/employees/search` endpoint is purpose-built for
 * this lookup and returns a narrower shape than the full Employees list.
 *
 * Mockup-mode branch searches the in-memory EMPLOYEES fixture. Live-mode
 * branch calls the search endpoint via React Query. The query is gated on
 * a non-empty trimmed email so an empty input does not fire a request.
 *
 * Search rules (mirrors the live contract):
 *   - Case-insensitive substring match against email
 *   - Only non-deactivated rows
 *   - Only rows not already linked to a Kanjoy user (mockup-mode filter —
 *     the live endpoint returns all matches; the modal already filters
 *     linked matches out of the callout by checking `userId !== null`)
 *   - Empty query returns no rows (match-as-you-type)
 *   - Caps at 20 results
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { EMPLOYEES, EMPLOYEES_DEACTIVATED_AT } from '@/data/employees';
import { KANJOY_USERS } from '@/data/kanjoyUsers';

export interface EmployeeSearchMatch {
  id: string;
  name: string;
  email: string;
  /** Kanjoy user id if the employee is already linked; null otherwise. */
  userId: string | null;
}

export interface UseEmployeeSearchResult {
  matches: readonly EmployeeSearchMatch[];
  isLoading: boolean;
}

interface ApiEmployeeSearchResult {
  employees: Array<{ id: string; name: string; email: string | null; userId: string | null }>;
}

const MAX_RESULTS = 20;

export function useEmployeeSearch(email: string): UseEmployeeSearchResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;
  const query = email.trim().toLowerCase();

  const mockMatches = useMemo<EmployeeSearchMatch[]>(() => {
    if (!mockupMode || !query) return [];

    // Pre-compute the linked-employee set once so we don't rescan
    // KANJOY_USERS for every row. Cheap for fixtures, matches the live
    // endpoint's intent of returning only unlinked employees.
    const linkedEmployeeIds = new Set<string>();
    for (const u of KANJOY_USERS) {
      if (u.employeeId && u.deactivatedAt === null) linkedEmployeeIds.add(u.employeeId);
    }

    const out: EmployeeSearchMatch[] = [];
    for (const emp of EMPLOYEES) {
      if (out.length >= MAX_RESULTS) break;
      if (EMPLOYEES_DEACTIVATED_AT[emp.id]) continue;
      if (linkedEmployeeIds.has(emp.id)) continue;
      const haystack = emp.email.toLowerCase();
      if (!haystack.includes(query)) continue;
      out.push({
        id: emp.id,
        name: emp.name,
        email: emp.email,
        userId: null,
      });
    }
    return out;
  }, [mockupMode, query]);

  const liveQuery = useQuery({
    queryKey: ['employeeSearch', orgId, query],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams({ email: query });
      const res = await apiFetch<ApiEmployeeSearchResult>(
        `/api/v1/orgs/${orgId}/employees/search?${qs.toString()}`,
        token,
      );
      return res.employees.map<EmployeeSearchMatch>((e) => ({
        id: e.id,
        name: e.name,
        email: e.email ?? '',
        userId: e.userId,
      }));
    },
    enabled: !mockupMode && !!orgId && query.length > 0,
  });

  if (mockupMode) {
    return { matches: mockMatches, isLoading: false };
  }

  return {
    matches: liveQuery.data ?? [],
    isLoading: liveQuery.isLoading,
  };
}
